import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Play, Pause, RotateCcw, SkipForward, Flag } from "lucide-react";
import TimerDisplay from "./TimerDisplay";
import PreCountdown from "./PreCountdown";
import { installShortcutListeners } from "@/utils/timerShortcuts";
import { unlockAudio } from "@/utils/timerSounds";
import { useI18n } from "@/context/I18nContext";
import { serverNow } from "@/utils/serverClock";

import { API } from '../../utils/apiBase';

export const CircleTimerPanel = ({ circle, athletes, timer, onUpdate }) => {
  const { t } = useI18n();
  const circleId = circle?.id;
  const advancedRef = useRef(false);

  useEffect(() => {
    if (!circleId) return;
    if (!timer || timer.status === "idle") {
      axios.post(`${API}/circle/${circleId}/timer/init`).then(() => onUpdate && onUpdate()).catch(() => {});
    }
    // eslint-disable-next-line
  }, [circleId]);

  const start = async () => {
    unlockAudio();
    await axios.post(`${API}/circle/${circleId}/timer/start`);
    if (onUpdate) onUpdate();
  };
  const pause = async () => {
    await axios.post(`${API}/circle/${circleId}/timer/pause`);
    if (onUpdate) onUpdate();
  };
  const resume = async () => {
    unlockAudio();
    await axios.post(`${API}/circle/${circleId}/timer/resume`);
    if (onUpdate) onUpdate();
  };
  const reset = async () => {
    await axios.post(`${API}/circle/${circleId}/timer/reset`);
    if (onUpdate) onUpdate();
  };
  const setSegment = async (round, position) => {
    await axios.post(`${API}/circle/${circleId}/timer/set-segment`, { round, position });
    if (onUpdate) onUpdate();
  };

  const skip = async () => {
    try {
      await axios.post(`${API}/circle/${circleId}/timer/skip`);
      if (onUpdate) onUpdate();
    } catch {}
  };

  const [endConfirm, setEndConfirm] = useState(false);
  const endTimer = async () => {
    if (!endConfirm) {
      setEndConfirm(true);
      setTimeout(() => setEndConfirm(false), 3000);
      return;
    }
    setEndConfirm(false);
    try {
      await axios.post(`${API}/circle/${circleId}/timer/end`);
      if (onUpdate) onUpdate();
    } catch {}
  };

  // Auto-advance when local countdown hits 0
  // Reset the "advanced" guard whenever a new segment starts (segment_started_at changes)
  useEffect(() => {
    advancedRef.current = false;
  }, [timer?.segment_started_at, timer?.segment_index]);

  useEffect(() => {
    if (!timer || timer.status !== "running") {
      return;
    }
    const checkAdvance = async () => {
      if (advancedRef.current) return;
      const duration = timer.duration_ms || 30000;
      let elapsed = timer.segment_paused_elapsed_ms || 0;
      if (timer.segment_started_at) {
        elapsed += serverNow() - new Date(timer.segment_started_at).getTime();
      }
      if (elapsed >= duration) {
        advancedRef.current = true;
        try {
          await axios.post(`${API}/circle/${circleId}/timer/advance`);
          if (onUpdate) onUpdate();
        } catch {}
      }
    };
    const iv = setInterval(checkAdvance, 200);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [timer?.status, timer?.segment_started_at, timer?.segment_paused_elapsed_ms, circleId]);

  // After pre_countdown elapsed → call begin-segments
  const onPreComplete = async () => {
    try {
      await axios.post(`${API}/circle/${circleId}/timer/begin-segments`);
      if (onUpdate) onUpdate();
    } catch {}
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!timer) return;
    const handlers = {
      play: () => {
        if (timer.status === "running") pause();
        else if (timer.status === "paused") resume();
        else if (timer.status === "pre_start") start();
      },
      stop: () => {},
      reset: () => reset(),
      escape: () => { if (timer.status === "running") pause(); },
    };
    return installShortcutListeners(handlers);
    // eslint-disable-next-line
  }, [timer?.status, circleId]);

  if (!circle) return null;
  const status = timer?.status || "idle";

  const sequence = timer?.sequence || [];
  const idx = timer?.segment_index || 0;
  const currentSeg = sequence[idx];
  const currentRound = currentSeg?.round;
  const currentPos = currentSeg?.position;
  const athleteAtPos = currentPos && circle.athlete_ids ? circle.athlete_ids[currentPos - 1] : null;
  const currentAthlete = athleteAtPos ? athletes.find(a => a?.id === athleteAtPos) : null;

  const totalRounds = Number(timer?.rounds) || 3;
  // Distinct positions in the sequence, in their canonical order (skipping forfeited)
  const availablePositions = Array.from(
    new Set((timer?.sequence || []).filter((s) => s.round === 1).map((s) => s.position))
  );

  return (
    <Card className="p-5 glass border-2 border-primary/30" data-testid="circle-timer-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-2xl font-bold text-foreground">⏱️ {t('timer.circleTimer')} {circle.circle_number}</h3>
        {timer && status !== "done" && status !== "idle" && currentSeg && (
          <div className="text-base text-muted-foreground">
            {t('timer.round')} <span className="text-foreground font-bold">{currentRound}</span> / {totalRounds}
            • {t('timer.position')} <span className="text-foreground font-bold">{currentPos}</span>
            {currentAthlete && <> • <span className="text-primary font-bold">{currentAthlete.name}</span></>}
          </div>
        )}
      </div>

      {/* Pre-countdown */}
      {status === "pre_countdown" && (
        <div className="flex justify-center my-4">
          <PreCountdown timer={timer} playSounds={false} onComplete={onPreComplete} />
        </div>
      )}

      {/* Running countdown */}
      {(status === "running" || status === "paused") && (
        <div className="flex justify-center my-4">
          <TimerDisplay timer={timer} variant="panel" playSounds={false} showProgress={false} />
        </div>
      )}

      {/* Pre-start: athletes shown big on display, manager triggers START */}
      {status === "pre_start" && (
        <div className="text-center py-4">
          <p className="text-base text-muted-foreground mb-3">{t('timer.athletesShown')}</p>
        </div>
      )}

      {/* Controls */}
      {status !== "done" && (
        <>
          {/* Manual round + position selectors (skip the pre_countdown / pre_start setup phase) */}
          {timer && timer.sequence && (status === "running" || status === "paused") && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <div className="text-xs text-muted-foreground mb-2 font-semibold uppercase tracking-wider">
                  {t('timer.roundSelector')}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {Array.from({ length: totalRounds }, (_, i) => i + 1).map((r) => (
                    <Button
                      key={r}
                      size="sm"
                      variant={currentRound === r ? "default" : "outline"}
                      onClick={() => setSegment(r, currentPos)}
                      data-testid={`circle-set-round-${r}`}
                      className="flex-1 min-w-16"
                    >
                      Round {r}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-muted/40 border border-border">
                <div className="text-xs text-muted-foreground mb-2 font-semibold uppercase tracking-wider">
                  {t('timer.positionSelector')}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {availablePositions.map((p) => (
                    <Button
                      key={p}
                      size="sm"
                      variant={currentPos === p ? "default" : "outline"}
                      onClick={() => setSegment(currentRound || 1, p)}
                      data-testid={`circle-set-position-${p}`}
                      className="flex-1 min-w-12"
                    >
                      P{p}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 justify-center mt-3">
            {status === "pre_start" && (
              <Button onClick={start} size="lg" className="bg-green-600 hover:bg-green-700" data-testid="circle-timer-start">
                <Play className="mr-2" size={20} /> {t('timer.startCircle')}
              </Button>
            )}
            {status === "running" && (
              <Button onClick={pause} variant="outline" size="lg" data-testid="circle-timer-pause">
                <Pause className="mr-2" size={20} /> {t('timer.pause')}
              </Button>
            )}
            {status === "paused" && (
              <Button onClick={resume} size="lg" className="bg-green-600 hover:bg-green-700" data-testid="circle-timer-resume">
                <Play className="mr-2" size={20} /> {t('timer.resume')}
              </Button>
            )}
            {(status === "running" || status === "paused") && (
              <Button onClick={reset} variant="outline" size="lg" data-testid="circle-timer-reset">
                <RotateCcw className="mr-2" size={20} /> {t('timer.resetSegment')}
              </Button>
            )}
            {(status === "running" || status === "paused") && (
              <Button
                onClick={skip}
                variant="outline"
                size="lg"
                className="border-amber-500 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-900/20"
                data-testid="circle-timer-skip"
              >
                <SkipForward className="mr-2" size={20} /> {t('timer.skipSegment')}
              </Button>
            )}
            {(status === "running" || status === "paused" || status === "pre_countdown" || status === "pre_start") && (
              <Button
                onClick={endTimer}
                variant="outline"
                size="lg"
                className={endConfirm
                  ? "bg-red-600 hover:bg-red-700 text-white border-red-600"
                  : "border-red-500 text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/20"}
                data-testid="circle-timer-end"
              >
                <Flag className="mr-2" size={20} />
                {endConfirm ? t('timer.endCircleConfirm') : t('timer.endCircle')}
              </Button>
            )}
          </div>
        </>
      )}

      {/* Done */}
      {status === "done" && (
        <div className="text-center py-3 text-green-600 dark:text-green-400 font-bold text-lg" data-testid="circle-timer-done">
          ✅ {t('timer.allRoundsDone')}
        </div>
      )}
    </Card>
  );
};

export default CircleTimerPanel;
